import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePublicBranding } from '../hooks/usePublicBranding';
import { hexToHslTriple } from '../contexts/BrandingContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import {
  Lock, Mail, ShieldCheck, Eye, EyeOff, ArrowRight,
  Users, CalendarCheck2, Wallet, TrendingUp, Clock, KeyRound,
} from 'lucide-react';
import { DEFAULT_FOOTER_OWNER } from '../components/AppFooter';
import { authAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const REMEMBERED_LOGIN_STORAGE_KEY = 'hrms_remembered_login';

const LoginPage = () => {
  // Resolved from ?w=, the last workspace signed into, or the hostname.
  const publicBranding = usePublicBranding();
  useEffect(() => {
    if (!publicBranding?.primary_color) return;
    // Same conversion the authenticated theme uses; the CSS variables are the
    // only thing either of them touches.
    const triple = hexToHslTriple(publicBranding.primary_color);
    if (triple) document.documentElement.style.setProperty('--primary', triple);
  }, [publicBranding]);
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaState, setMfaState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // 'password' | 'otp'. The one-time code is a second way in, not a second step.
  const [mode, setMode] = useState('password');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const { login, verifyMfaChallenge, loginWithOtp } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(REMEMBERED_LOGIN_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (parsed?.email) setEmail(String(parsed.email));
      if (parsed?.password) setPassword(String(parsed.password));
      if (parsed?.email || parsed?.password) {
        setRememberPassword(true);
      }
    } catch (error) {
      window.localStorage.removeItem(REMEMBERED_LOGIN_STORAGE_KEY);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (rememberPassword) {
        window.localStorage.setItem(
          REMEMBERED_LOGIN_STORAGE_KEY,
          JSON.stringify({
            email,
            password,
          })
        );
      } else {
        window.localStorage.removeItem(REMEMBERED_LOGIN_STORAGE_KEY);
      }

      const result = await login(email, password);
      if (result?.access_token) {
        toast.success('Login successful');
        navigate('/dashboard');
        return;
      }

      if (result?.status === 'mfa_required' || result?.status === 'mfa_enrollment_required') {
        setMfaState(result);
        setMfaCode('');
        toast.success(
          result?.status === 'mfa_enrollment_required'
            ? 'MFA setup required. Add the new setup key before entering a code.'
            : 'MFA challenge issued'
        );
        return;
      }

      toast.error('Unable to continue login');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await verifyMfaChallenge(mfaState?.mfa_challenge_token, mfaCode, email);
      if (result?.access_token) {
        toast.success('MFA verification successful');
        navigate('/dashboard');
        return;
      }
      toast.error('MFA verification did not complete login');
    } catch (error) {
      toast.error(getErrorMessage(error, 'MFA verification failed'));
    } finally {
      setLoading(false);
    }
  };

  const resetMfaFlow = () => {
    setMfaState(null);
    setMfaCode('');
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await authAPI.requestLoginOtp(email);
      setOtpSent(true);
      setOtpCode('');
      toast.success(data?.detail || 'If the account exists, a login code has been sent.');
      // Non-production only: the server withholds this on live deployments.
      if (data?.debug_code) toast.info(`Development code: ${data.debug_code}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not send the code'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginWithOtp(email, otpCode);
      if (result?.access_token) {
        toast.success('Login successful');
        navigate('/dashboard');
        return;
      }
      toast.error('Sign-in did not complete');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid or expired code'));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setOtpSent(false);
    setOtpCode('');
  };

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-2">
      {/* Left — sign-in form on a clean light surface */}
      <div className="relative flex min-h-screen items-center justify-center bg-background px-5 py-16 sm:px-8 lg:px-12">
          <Card className="w-full max-w-md border-0 bg-transparent shadow-none animate-fade-in" data-testid="login-card">
              <CardHeader className="space-y-4 px-0 text-left">
                <div className="space-y-3">
                  {publicBranding?.logo_url ? (
                    <img
                      src={publicBranding.logo_url}
                      alt={publicBranding.app_name || 'Workspace'}
                      className="h-9 w-auto max-w-[220px] object-contain"
                    />
                  ) : (
                    <>
                      <img src="/web-hr-logo.png" alt="WebHR" className="h-9 w-auto dark:hidden" />
                      <img src="/web-hr-logo-white.png" alt="WebHR" className="hidden h-9 w-auto dark:block" />
                    </>
                  )}
                  <CardDescription className="text-sm text-muted-foreground">
                    {mfaState
                      ? 'Multi-factor verification required'
                      : mode === 'otp'
                        ? 'Sign in with a one-time code.'
                        : 'Welcome back. Sign in to continue.'}
                  </CardDescription>
                </div>

              {/*   {!mfaState ? (
                  <div className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Remembered credentials available when enabled on this device.
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Toggle password visibility before logging in.
                    </div>
                  </div>
                ) : null} */}
              </CardHeader>

              <CardContent className="px-0">
                {!mfaState && mode === 'otp' ? (
                  <form onSubmit={otpSent ? handleVerifyOtp : handleRequestOtp} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="otp-identifier">Email or mobile number</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="otp-identifier"
                          type="text"
                          placeholder="name@yourcompany.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onFocus={() => setOtpSent(false)}
                          required
                          className="h-12 rounded-xl border-border pl-10"
                          data-testid="otp-identifier-input"
                        />
                      </div>
                    </div>

                    {otpSent ? (
                      <div className="space-y-2">
                        <Label>Sign-in code</Label>
                        <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                          <InputOTPGroup className="w-full justify-center">
                            {Array.from({ length: 6 }).map((_, index) => (
                              <InputOTPSlot key={index} index={index} className="h-12 w-12 rounded-xl text-base" />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                        <button
                          type="button"
                          onClick={handleRequestOtp}
                          disabled={loading}
                          className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
                        >
                          Send a new code
                        </button>
                      </div>
                    ) : null}

                    <Button
                      type="submit"
                      className="h-12 w-full rounded-xl text-base font-semibold shadow-lg transition-all duration-200 hover:shadow-xl active:scale-[0.99]"
                      disabled={loading || (otpSent && String(otpCode).length !== 6)}
                      data-testid="otp-submit-button"
                    >
                      <span>
                        {loading
                          ? (otpSent ? 'Verifying...' : 'Sending...')
                          : (otpSent ? 'Verify and sign in' : 'Send code')}
                      </span>
                      {!loading ? <ArrowRight className="ml-2 h-4 w-4" /> : null}
                    </Button>

                    <button
                      type="button"
                      onClick={() => switchMode('password')}
                      className="flex w-full items-center justify-center gap-2 text-sm font-medium text-primary transition-colors hover:text-primary/80"
                    >
                      <Lock className="h-4 w-4" />
                      Sign in with a password
                    </button>
                  </form>
                ) : null}

                {!mfaState && mode === 'password' ? (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="name@yourcompany.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="h-12 rounded-xl border-border pl-10"
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
                          data-testid="password-visibility-toggle"
                        >
                          {showPassword ? 'Hide password' : 'Show password'}
                        </button>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="h-12 rounded-xl border-border pl-10 pr-12"
                          data-testid="password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-3 top-3 inline-flex h-6 w-6 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted px-4 py-3">
                      <label className="flex cursor-pointer items-center gap-3 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={rememberPassword}
                          onChange={(e) => setRememberPassword(e.target.checked)}
                          className="h-4 w-4 rounded border-border"
                          data-testid="remember-password-checkbox"
                        />
                        Remember password on this device
                      </label>
                      <span className="text-xs text-muted-foreground">Optional</span>
                    </div>

                    <Button
                      type="submit"
                      className="h-12 w-full rounded-xl text-base font-semibold shadow-lg transition-all duration-200 hover:shadow-xl active:scale-[0.99]"
                      disabled={loading}
                      data-testid="login-submit-button"
                    >
                      <span>{loading ? 'Logging in...' : 'Login'}</span>
                      {!loading ? <ArrowRight className="ml-2 h-4 w-4" /> : null}
                    </Button>

                    <button
                      type="button"
                      onClick={() => switchMode('otp')}
                      className="flex w-full items-center justify-center gap-2 text-sm font-medium text-primary transition-colors hover:text-primary/80"
                      data-testid="otp-mode-button"
                    >
                      <KeyRound className="h-4 w-4" />
                      Login with OTP
                    </button>
                  </form>
                ) : null}

                {mfaState ? (
                  <form onSubmit={handleVerifyMfa} className="space-y-5">
                    <div className="rounded-2xl border border-primary/15 bg-primary/5 p-4">
                      <div className="flex items-start gap-3">
                        <ShieldCheck className="mt-0.5 h-5 w-5 text-primary" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-foreground">
                            {mfaState?.mfa_setup_required ? 'Set up authenticator app' : 'Enter MFA verification code'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {mfaState?.mfa_setup_required
                              ? 'Add this account to your authenticator app using the new setup key below. Existing authenticator codes will not work until the new key is saved.'
                              : 'Use the 6-digit code from your authenticator app to complete sign-in.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {mfaState?.mfa_setup_required ? (
                      <div className="space-y-2">
                        <Label>Setup Key</Label>
                        <div className="rounded-xl border bg-muted px-3 py-3 font-mono text-sm break-all text-foreground">
                          {mfaState?.mfa_setup_secret}
                        </div>
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <Label>MFA Code</Label>
                      <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode}>
                        <InputOTPGroup className="w-full justify-center">
                          {Array.from({ length: 6 }).map((_, index) => (
                            <InputOTPSlot key={index} index={index} className="h-12 w-12 rounded-xl text-base" />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 rounded-xl"
                        onClick={resetMfaFlow}
                        disabled={loading}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 rounded-xl"
                        disabled={loading || String(mfaCode).length !== 6}
                      >
                        {loading ? 'Verifying...' : 'Verify MFA'}
                      </Button>
                    </div>
                  </form>
                ) : null}

                <div className="mt-6 text-center text-sm text-muted-foreground">
                  <p>
                    {mfaState
                      ? 'MFA is enforced for this account before the session is issued.'
                      : mode === 'otp'
                        ? 'We send a 6-digit code to your work email. It expires in 10 minutes.'
                        : 'Use your provisioned work account credentials to sign in.'}
                  </p>
                </div>
              </CardContent>
            </Card>

        {/* Page footer. The right panel carries the attribution on large
            screens, where it is hidden here to stay off the fold line. */}
        <div className="absolute inset-x-5 bottom-5 flex items-center justify-between gap-4 text-xs text-muted-foreground sm:inset-x-8 lg:inset-x-12">
          {/* No tenant is known before sign-in, so this is the one page that
              always shows the platform owner. */}
          <span>{currentYear} - {DEFAULT_FOOTER_OWNER}. All rights reserved.</span>
          <span className="font-medium lg:hidden">Powered by Appable</span>
        </div>
      </div>

      {/* Right — HRMS-themed brand visual (hidden on small screens) */}
      <div className="relative hidden overflow-hidden bg-primary text-white lg:block">
        {/* Brand gradient wash */}
        <div className="absolute inset-0 bg-[linear-gradient(150deg,hsl(var(--primary))_0%,hsl(var(--primary-hover))_52%,#082a63_100%)]" />
        {/* Fine dot grid — an HRMS-aligned pattern */}
        <div className="absolute inset-0 opacity-[0.35] bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.35)_1px,transparent_0)] bg-[size:22px_22px]" />
        {/* Soft light blooms */}
        <div className="absolute -left-16 top-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -right-10 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl" />

        <div className="relative z-10 flex h-full min-h-screen flex-col justify-between p-12 xl:p-16">
          {/* Headline + product collage — centred now that the wordmark above it is gone */}
          <div className="my-auto max-w-lg space-y-8">
            <div className="space-y-4 animate-slide-up">
              <h1 className="text-4xl font-bold leading-[1.15] tracking-tight xl:text-5xl">
                Everything your people team needs, in one place.
              </h1>
              <p className="text-base leading-relaxed text-white/80">
                Attendance, payroll, performance and workforce analytics — unified in a single,
                secure command center your whole company can trust.
              </p>
            </div>

            {/* Floating module preview card */}
            <div className="w-full rounded-3xl border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-md animate-scale-in">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Today at a glance</p>
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white/85">Live</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {[
                  { icon: Users, label: 'Employees', value: '1,248' },
                  { icon: CalendarCheck2, label: 'On-time today', value: '98.6%' },
                  { icon: Wallet, label: 'Payroll ready', value: '1-click' },
                  { icon: TrendingUp, label: 'Engagement', value: '+4.2%' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="rounded-2xl bg-white/10 p-3.5 ring-1 ring-white/10">
                    <Icon className="h-5 w-5 text-white/85" />
                    <p className="mt-2 text-xl font-semibold leading-none">{value}</p>
                    <p className="mt-1 text-xs text-white/70">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Trust row, with the page's bottom-right attribution */}
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm text-white/80 animate-fade-in">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Enterprise-grade security</span>
              <span className="inline-flex items-center gap-2"><Lock className="h-4 w-4" /> MFA enforced</span>
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4" /> 24/7 availability</span>
            </div>
            {/* ml-auto keeps it right-aligned on the narrow widths where the
                trust row wraps and justify-between stops applying. */}
            <span className="ml-auto text-xs font-medium text-white/70">Powered by Appable</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
